import { CartResult, getCart } from "@/api/woocommerce/Cart";
import { CheckoutDraftType, TotalsType } from "@/api/woocommerce/Cart/models";
import { EnrichedCartItemType } from "@/models/cart";
import { isValidJwt } from "@/utils/functions/token";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type TokensType = {
  cartToken: string | null;
  nonce: string | null;
};

export type CartType = {
  items: EnrichedCartItemType[];
  coupons: any[];
  fees: any[];
  totals: TotalsType;
  shipping_address: {
    first_name: string;
    last_name: string;
    company: string;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    phone: string;
  };
  billing_address: {
    first_name: string;
    last_name: string;
    company: string;
    address_1: string;
    address_2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    email: string;
    phone: string;
  };
  needs_payment: boolean;
  needs_shipping: boolean;
  payment_requirements: string[];
  has_calculated_shipping: boolean;
  shipping_rates: any[];
  items_count: number;
  items_weight: number;
  cross_sells: any[];
  errors: any[];
  payment_methods: any[];
  extensions: Record<string, any>;
};

interface CartStoreState {
  checkoutDraft: CheckoutDraftType | null; // Holds the checkout draft information
  setCheckoutDraft: (draft: CheckoutDraftType | null) => void; // Sets the checkout draft in the store
  storeTokens: TokensType;
  setStoreTokens: (tokens: TokensType) => void; // Sets the cart token and nonce in the store
  clearStoreCache: () => void; // Clears the cart, cart token and nonce
  cart: CartType | null;
  setCart: (cart: CartType | null) => void; // Sets the cart in the store
  initiateCartSession: () => void; // Takes either a cart token and nonce (token object) or nothing to get a brand new cart}
}

export const useCartStore = create<CartStoreState>()(
  persist(
    (set, get) => ({
      checkoutDraft: null,
      setCheckoutDraft: (draft) => set({ checkoutDraft: draft }),
      storeTokens: { cartToken: null, nonce: null, expiry: null },
      setStoreTokens: (tokens: TokensType) => set({ storeTokens: tokens }), // TODO add expiry handling
      clearStoreCache: () => {
        set({ storeTokens: { cartToken: null, nonce: null } });
        set({ cart: null });
        set({ checkoutDraft: null });
      },
      cart: null,
      setCart: (cart) => set({ cart }),
      initiateCartSession: async () => {
        try {
          const { storeTokens } = get();
          const { cartToken, nonce } = storeTokens || {};
          let response: CartResult;

          const isValid = isValidJwt(cartToken);

          if (cartToken && nonce && isValid === true) {
            response = await getCart({ cartToken, nonce });
          } else {
            response = await getCart({ cartToken: null, nonce: null });
          }

          if (response) {
            if ("error" in response) {
              console.error("Error fetching cart:", response.message);
              set({ cart: null });
            } else {
              set({ cart: response.cart });
              if (response.cartToken && response.nonce) {
                set({
                  storeTokens: {
                    cartToken: response.cartToken,
                    nonce: response.nonce,
                  },
                });
              }
            }
          }
        } catch (error) {
          console.error("Error initiating cart session:", error);
          set({ cart: null });
          set({ storeTokens: { cartToken: null, nonce: null } });
        }
      },
    }),
    {
      name: `on-demand-cart-store`,
      storage: createJSONStorage(() => localStorage),
    }
  )
);
